import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiService, setCsrfToken } from "@/services/api.service";
import { User } from "@/types/user.types";
import { ToastFunction } from "@/types/toast.types";
import type { UserRolePermission } from "@/types/rbac.types";

interface LoginResult {
  accessToken?: string; // legacy: backend returns it during transition; cookie is the source of truth
  expiresAt: string;
  userId: string;
  user: { id: string; email: string; name: string | null; roles: UserRolePermission[] };
  csrfToken?: string; // double-submit token — fica em memoria (per-domain cookies não dão pra ler)
}

interface LoginWith2faRequired {
  requiresTwoFactor: true;
  tempToken: string;
}

type LoginResponse = LoginResult | LoginWith2faRequired;

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isInitialized: boolean;

  // 2FA intermediário
  tempToken: string | null;
  requires2fa: boolean;

  login: (email: string, password: string, toast: ToastFunction, recaptchaToken?: string) => Promise<boolean>;
  loginWithGoogle: (credential: string, toast: ToastFunction) => Promise<boolean>;
  loginWith2fa: (code: string, toast: ToastFunction) => Promise<boolean>;
  logout: (toast: ToastFunction) => Promise<void>;
  initialize: () => Promise<void>;
  clearTwoFactor: () => void;

  setUser: (user: User | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsInitialized: (isInitialized: boolean) => void;
}

/**
 * Non-sensitive marker used only for cross-tab session sync.
 * The real session is in an HttpOnly cookie (`liqicaas.token`) that JS cannot read.
 * This marker fires `storage` events on other tabs when login/logout happens.
 */
const SESSION_MARKER_KEY = "caas:session-active";

function setSessionMarker(active: boolean): void {
  if (typeof localStorage === "undefined") return;
  if (active) {
    localStorage.setItem(SESSION_MARKER_KEY, "1");
  } else {
    localStorage.removeItem(SESSION_MARKER_KEY);
  }
}

/**
 * Cross-tab session sync: when another tab logs out or starts a new session,
 * this tab reacts immediately (redirect to /login or reload).
 */
function setupCrossTabSync(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key !== SESSION_MARKER_KEY) return;

    if (!event.newValue) {
      // Marker removed in another tab = logout or inactivity timeout
      window.location.href = "/login?reason=session-ended";
    } else if (event.newValue !== event.oldValue) {
      // Marker changed = different session, reload to pick up new user
      window.location.reload();
    }
  });
}

setupCrossTabSync();

export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      user: null,
      isLoading: false,
      isInitialized: false,
      tempToken: null,
      requires2fa: false,

      setUser: (user) => set({ user }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setIsInitialized: (isInitialized) => set({ isInitialized }),

      clearTwoFactor: () => set({ tempToken: null, requires2fa: false }),

      initialize: async () => {
        const state = get();
        if (state.isInitialized || state.isLoading) return;

        set({ isLoading: true });

        try {
          const response = await apiService.get<{
            id: string;
            email: string;
            name: string | null;
            roles?: UserRolePermission[];
            isGoogle?: boolean;
            googleId?: string | null;
            companyId?: string | null;
            csrfToken?: string | null;
          }>("/auth/me");

          if (response.ok && response.data) {
            setSessionMarker(true);
            // Hidrata csrfToken da response — necessário após hard reload,
            // onde a memoria do api.service zera mas o cookie cross-domain
            // não é legível via document.cookie. Sem isso, primeira mutation
            // após reload falharia com 403 CSRF.
            if (response.data.csrfToken) {
              setCsrfToken(response.data.csrfToken);
            }
            set({
              user: {
                id: response.data.id,
                email: response.data.email,
                name: response.data.name ?? "",
                status: "ACTIVE",
                roles: response.data.roles ?? [],
                isGoogle: response.data.isGoogle ?? false,
                googleId: response.data.googleId ?? null,
                companyId: response.data.companyId ?? null,
              },
              isInitialized: true,
              isLoading: false,
            });
          } else {
            setSessionMarker(false);
            set({ user: null, isInitialized: true, isLoading: false });
          }
        } catch {
          setSessionMarker(false);
          set({ user: null, isInitialized: true, isLoading: false });
        }
      },

      login: async (email: string, password: string, toast: ToastFunction, recaptchaToken?: string) => {
        set({ isLoading: true, requires2fa: false, tempToken: null });

        try {
          const extraHeaders: Record<string, string> = {};
          if (recaptchaToken) {
            extraHeaders["x-recaptcha-token"] = recaptchaToken;
          }

          const response = await apiService.post<LoginResponse>(
            "/auth/login",
            { email, password },
            { headers: extraHeaders },
          );

          if (!response.ok || !response.data) {
            throw new Error(response.error || "Falha na autenticação");
          }

          const data = response.data;

          // 2FA necessário
          if ("requiresTwoFactor" in data && data.requiresTwoFactor) {
            set({ isLoading: false, requires2fa: true, tempToken: data.tempToken });
            return false;
          }

          const result = data as LoginResult;
          setSessionMarker(true);
          setCsrfToken(result.csrfToken);

          set({
            user: {
              id: result.user.id,
              email: result.user.email,
              name: result.user.name ?? "",
              status: "ACTIVE",
              roles: result.user.roles ?? [],
            },
            isLoading: false,
          });

          toast({
            title: "Login realizado com sucesso!",
            description: `Bem-vindo(a), ${result.user.name || result.user.email}!`,
          });

          return true;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Falha na autenticação";
          set({ isLoading: false });

          toast({
            title: "Erro no login",
            description: errorMessage,
            variant: "destructive",
          });

          return false;
        }
      },

      loginWithGoogle: async (credential: string, toast: ToastFunction) => {
        set({ isLoading: true, requires2fa: false, tempToken: null });
        try {
          const response = await apiService.post<LoginResponse>("/auth/google", { credential });

          if (!response.ok || !response.data) {
            throw new Error(response.error || "Falha na autenticação com Google");
          }

          const data = response.data;

          if ("requiresTwoFactor" in data && data.requiresTwoFactor) {
            set({ isLoading: false, requires2fa: true, tempToken: data.tempToken });
            return false;
          }

          const result = data as LoginResult;
          setSessionMarker(true);
          setCsrfToken(result.csrfToken);

          set({
            user: {
              id: result.user.id,
              email: result.user.email,
              name: result.user.name ?? "",
              status: "ACTIVE",
              roles: result.user.roles ?? [],
            },
            isLoading: false,
          });

          toast({
            title: "Login realizado com sucesso!",
            description: `Bem-vindo(a), ${result.user.name || result.user.email}!`,
          });

          return true;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Falha na autenticação com Google";
          set({ isLoading: false });
          toast({ title: "Erro no login", description: errorMessage, variant: "destructive" });
          return false;
        }
      },

      loginWith2fa: async (code: string, toast: ToastFunction) => {
        const { tempToken } = get();
        if (!tempToken) return false;

        set({ isLoading: true });

        try {
          const response = await apiService.post<LoginResult>("/auth/2fa/login", { tempToken, code });

          if (!response.ok || !response.data) {
            throw new Error(response.error || "Código 2FA inválido");
          }

          const result = response.data;
          setSessionMarker(true);
          setCsrfToken(result.csrfToken);

          set({
            user: {
              id: result.user.id,
              email: result.user.email,
              name: result.user.name ?? "",
              status: "ACTIVE",
              roles: result.user.roles ?? [],
            },
            isLoading: false,
            requires2fa: false,
            tempToken: null,
          });

          toast({
            title: "Login realizado com sucesso!",
            description: `Bem-vindo(a), ${result.user.name || result.user.email}!`,
          });

          return true;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : "Código 2FA inválido";
          set({ isLoading: false });

          toast({
            title: "Erro na verificação 2FA",
            description: errorMessage,
            variant: "destructive",
          });

          return false;
        }
      },

      logout: async (toast: ToastFunction) => {
        try {
          await apiService.post("/auth/logout");
        } catch (error) {
          console.error("Erro ao fazer logout:", error);
        }

        setSessionMarker(false);
        setCsrfToken(null);
        set({ user: null, requires2fa: false, tempToken: null });

        toast({
          title: "Logout realizado",
          description: "Você foi desconectado com sucesso.",
        });
      },
    }),
    {
      name: "AuthStore",
      enabled: import.meta.env.DEV,
    },
  ),
);

export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => !!state.user);
export const useIsLoading = () => useAuthStore((state) => state.isLoading);
export const useIsInitialized = () => useAuthStore((state) => state.isInitialized);
export const useRequires2fa = () => useAuthStore((state) => state.requires2fa);

export const useLogin = () => useAuthStore((state) => state.login);
export const useLoginWithGoogle = () => useAuthStore((state) => state.loginWithGoogle);
export const useLoginWith2fa = () => useAuthStore((state) => state.loginWith2fa);
export const useLogout = () => useAuthStore((state) => state.logout);
export const useInitialize = () => useAuthStore((state) => state.initialize);
export const useClearTwoFactor = () => useAuthStore((state) => state.clearTwoFactor);
